/**
 * `useToast` / `toast` — compatibility adapter over sonner.
 *
 * The app used to run two notification systems side by side: the shadcn
 * Radix toaster (this hook, `toast({ title, description, variant })`) and
 * sonner (`toast.success("…")`). They rendered in different corners with
 * different styling and stacking rules. Everything now goes through
 * sonner; this module keeps the old object-style API so the ~45 existing
 * call sites don't need to change.
 *
 *   toast({ title: "Saved" })
 *   toast({ title: "Update failed", description: e.message, variant: "destructive" })
 *   const { toast } = useToast()
 *
 * New code should import `toast` from "sonner" directly.
 */
import * as React from "react"
import { toast as sonner } from "sonner"

export type ToastVariant =
  | "default"
  | "destructive"
  | "success"
  | "warning"
  | "info"

export interface ToastOptions {
  title?: React.ReactNode
  description?: React.ReactNode
  variant?: ToastVariant
  /** Optional element rendered as the toast action (e.g. an Undo button). */
  action?: React.ReactNode
  duration?: number
  /** Ignored — kept so spread call sites still type-check. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export interface ToastHandle {
  id: string | number
  dismiss: () => void
  update: (next: ToastOptions) => void
}

function show(opts: ToastOptions, id?: string | number): string | number {
  const { title, description, variant = "default", action, duration } = opts
  const message = title ?? description ?? ""
  const data = {
    id,
    description: title ? description : undefined,
    action: action as never,
    duration,
  }
  switch (variant) {
    case "destructive":
      return sonner.error(message, data)
    case "success":
      return sonner.success(message, data)
    case "warning":
      return sonner.warning(message, data)
    case "info":
      return sonner.info(message, data)
    default:
      return sonner(message, data)
  }
}

function toast(opts: ToastOptions): ToastHandle {
  const id = show(opts)
  return {
    id,
    dismiss: () => sonner.dismiss(id),
    update: (next) => void show({ ...opts, ...next }, id),
  }
}

function useToast() {
  return {
    toast,
    dismiss: (id?: string | number) => sonner.dismiss(id),
    /** Always empty: sonner owns the queue now. Kept for API shape. */
    toasts: [] as ToastOptions[],
  }
}

export { useToast, toast }
